import type { ReactNode } from 'react'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { setPreference, getPreference } from '@fs/zion-preferences'
import { useEvent, useSyncedRef } from '@fs/zion-frontend-friends'
import { ContrastModeContext } from '@fs/zion-ui/quarks-authorized-use-only'
import { localStorageGetItem, localStorageRemoveItem, localStorageSetItem } from './utils/localStorage'

// Use user preference so that contrast mode is set in all browsers used by user
// Use localStorage so that if user is in contrast mode, they do not see things in normal mode first while waiting for the user preference.

export type ContrastModeValue = { isHighContrast: boolean; setHighContrast: (value: boolean) => void }

const contrastModeKey = 'tree.person-page.contrast-mode'

function setLocalStoragePreference(value: boolean): void {
  if (value) localStorageSetItem(contrastModeKey, 'true')
  else localStorageRemoveItem(contrastModeKey)
}

function useContrastState(): ContrastModeValue {
  const [isHighContrast, setIsHighContrast] = useState(() => localStorageGetItem(contrastModeKey) === 'true')

  const isHighContrastRef = useSyncedRef(isHighContrast)

  const setHighContrast = useEvent((value: boolean) => {
    setIsHighContrast(value)
    setPreference(contrastModeKey, value)
    setLocalStoragePreference(value)
  })

  useEffect(() => {
    let isCurrent = true
    getPreference<boolean>(contrastModeKey)
      .then((contrastModePref) => {
        const value = !!contrastModePref
        if (isCurrent && value !== isHighContrastRef.current && contrastModePref !== null) {
          setIsHighContrast(value)
          setLocalStoragePreference(value)
        }
        return undefined
      })
      .catch(console.error)
    return () => {
      isCurrent = false
    }
  }, [isHighContrastRef])

  return { isHighContrast, setHighContrast }
}

const SetContrastModeContext = createContext<ContrastModeValue['setHighContrast']>(() => undefined)

export const useContrastMode = (): ContrastModeValue => {
  const isHighContrast = useContext(ContrastModeContext)
  const setHighContrast = useContext(SetContrastModeContext)
  return { isHighContrast, setHighContrast }
}

type Props = {
  value?: ContrastModeValue
  children: ReactNode
}
export default function ContrastModeProvider({ value, children }: Props): JSX.Element {
  const contrastModeValue = useContrastState()
  return (
    <ContrastModeContext.Provider value={value?.isHighContrast ?? contrastModeValue?.isHighContrast}>
      <SetContrastModeContext.Provider value={value?.setHighContrast ?? contrastModeValue?.setHighContrast}>
        {children}
      </SetContrastModeContext.Provider>
    </ContrastModeContext.Provider>
  )
}
